/**
 * @file RepContactCard.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description One customer as a card on a rep's phone (Search Contacts).
 *              Cam, 29 Oct 2026: "Checking the rep mobile view: can the
 *              contact cards look more like the order cards etc. please? Have
 *              a look via screenshots and make uniform."
 *
 * The list used to be the desktop table restyled by CSS — every cell a
 * label / value row ("They owe –", "We owe –", "Credit limit –"), the action
 * column pulled out of the flow with absolute positioning and then hidden.
 * This is RepDocCard, the shell the order, invoice, PO and standing order
 * cards share (RepOrderCard.css), so it reads the same:
 *  - the card itself is ONE button that opens the customer (the chevron says
 *    so) — no hover state, nothing expands on a tap;
 *  - the account badge and the name, where the customer is under them;
 *  - what they owe us in words (Overdue / Not yet due / Nothing owed) with
 *    the figure on the right, as an invoice card shows its total;
 *  - Call and Email along the foot, each its own labelled 44px button —
 *    greyed with the reason when the account has no number or address.
 *
 * Words in utils/repContactCard. Read only, like everything a rep sees: a
 * call or an email writes nothing here.
 */

import React from 'react';
import RepDocCard from './RepDocCard';
import { repContactCard } from '../utils/repContactCard';

/** Follow a tel: / mailto: link — the phone's own dialler or mail app. */
const follow = (href) => { window.location.href = href; };

function RepContactCard({ contact, onOpen }) {
  const card = repContactCard(contact);
  return (
    <RepDocCard
      kind={card.kind}
      badge={card.badge}
      title={card.title}
      place={card.place}
      flags={card.flags}
      chip={card.chip}
      extraChips={card.extraChips}
      total={card.total}
      totalSub={card.totalSub}
      archived={card.archived}
      onOpen={onOpen || undefined}
      openLabel={`Open ${card.title}${card.badge ? ` (${card.badge})` : ''}`}
      actions={[
        {
          key: 'call',
          label: 'Call',
          icon: 'call',
          onClick: card.phone ? () => follow(card.phone.href) : null,
          title: card.phone ? `${card.phone.label} ${card.phone.display}` : undefined,
          disabledTitle: 'No phone number on this account'
        },
        {
          key: 'email',
          label: 'Email',
          icon: 'mail',
          onClick: card.email ? () => follow(card.email.href) : null,
          title: card.email ? card.email.address : undefined,
          disabledTitle: 'No email address on this account'
        }
      ]}
    >
      {card.creditText && (
        <span className="rdc-lines">
          <span className="rdc-line is-muted">{card.creditText}</span>
        </span>
      )}
    </RepDocCard>
  );
}

export default RepContactCard;
